<template>
  <div class="job-manager-schedules">
    <v-container fluid>
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <h1 class="text-h4">{{ $t('job-manager.schedules-title') }}</h1>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end align-center">
          <v-btn
            small
            color="success"
            class="mr-2"
            @click="openCreateDialog"
          >
            <v-icon left small>mdi-plus</v-icon>
            {{ $t('job-manager.schedules.create') }}
          </v-btn>
          <v-btn
            small
            color="primary"
            :loading="executingOverdue"
            @click="executeOverdue"
          >
            <v-icon left small>mdi-play</v-icon>
            {{ $t('job-manager.schedules.execute-overdue') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row>
        <v-col cols="12">
          <v-card class="mb-4">
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="filters.status"
                    :items="statusOptions"
                    :label="$t('job-manager.schedules.filter-status')"
                    clearable
                    @change="loadSchedules"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="filters.type"
                    :items="typeOptions"
                    :label="$t('job-manager.schedules.filter-type')"
                    clearable
                    @change="loadSchedules"
                  />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn small text @click="resetFilters">
                    <v-icon left small>mdi-filter-off</v-icon>
                    {{ $t('job-manager.reset-filters') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Recurring Tasks (Cron-based schedules) -->
      <v-row>
        <v-col cols="12">
          <headline>{{ $t('job-manager.recurring-tasks') }}</headline>
          <v-data-table
            :headers="recurringHeaders"
            :items="recurringTasks"
            :items-per-page="10"
            :loading="loading"
            class="elevation-1"
          >
            <!-- Definition Column -->
            <template v-slot:item.jobDefinitionId="{ item }">
              <v-tooltip bottom :disabled="!item.jobDefinitionDescription" max-width="360">
                <template v-slot:activator="{ on }">
                  <span class="font-weight-medium" v-on="on">{{ getJobDefinitionName(item) }}</span>
                </template>
                <span>{{ item.jobDefinitionDescription }}</span>
              </v-tooltip>
            </template>

            <!-- Cron Expression Column -->
            <template v-slot:item.cronExpression="{ item }">
              <div class="d-flex flex-column">
                <span class="text-body-2">{{ item.cronExpressionHuman || getCronDescription(item.cronExpression) }}</span>
                <code class="text-caption text--secondary mt-1">
                  {{ item.cronExpression }}<span v-if="item.timezone"> · {{ item.timezone }}</span>
                </code>
              </div>
            </template>

            <!-- Enabled Column -->
            <template v-slot:item.enabled="{ item }">
              <div class="d-flex flex-column align-start">
                <v-switch
                  :input-value="item.enabled"
                  @change="toggleEnabled(item)"
                  :loading="item._loading"
                  color="success"
                  hide-details
                  dense
                  class="mt-0"
                />
                <span v-if="item.pausedAt" class="text-caption text--secondary">
                  {{ $t('job-manager.schedules.paused-since', { date: formatDateTime(item.pausedAt) }) }}
                </span>
              </div>
            </template>

            <!-- Next Run Column -->
            <template v-slot:item.nextRunAt="{ item }">
              <div>
                <div :class="{ 'error--text font-weight-bold': isOverdue(item) }">
                  {{ formatDateTimeInTz(item.nextRunAt, item.timezone) }}
                  <span class="text-caption text--secondary">{{ item.timezone || 'UTC' }}</span>
                  <v-icon v-if="isOverdue(item)" small color="error" class="ml-1">mdi-alert</v-icon>
                </div>
                <div
                  v-if="item.nextRunAt && (item.timezone || 'UTC') !== browserTimezone()"
                  class="text-caption text--secondary"
                >
                  {{ $t('job-manager.schedules.local-time', { time: formatDateTime(item.nextRunAt) }) }}
                </div>
              </div>
            </template>

            <!-- Last Run Column -->
            <template v-slot:item.lastRunAt="{ item }">
              <div class="d-flex flex-column">
                <div class="d-flex align-center">
                  <v-tooltip bottom v-if="item.lastRunStatus">
                    <template #activator="{on, attrs}">
                      <v-chip
                        x-small
                        :color="getRunStatusColor(item.lastRunStatus)"
                        text-color="white"
                        class="mr-2"
                        :to="item.lastJobId ? { name: 'job-manager.jobs', query: { jobId: item.lastJobId } } : null"
                        v-on="on"
                        v-bind="attrs"
                      >
                        {{ item.lastRunStatus }}
                      </v-chip>
                    </template>
                    <span>{{ item.lastJobId ? $t('job-manager.schedules.open-last-job') : item.lastRunStatus }}</span>
                  </v-tooltip>
                  <span class="text-caption">{{ formatDateTime(item.lastRunAt) }}</span>
                </div>
                <v-chip
                  v-if="item.consecutiveFailures > 0"
                  x-small
                  color="error"
                  text-color="white"
                  class="mt-1"
                  outlined
                >
                  <v-icon left x-small>mdi-alert</v-icon>
                  {{ $tc('job-manager.schedules.consecutive-failures', item.consecutiveFailures, { count: item.consecutiveFailures }) }}
                </v-chip>
              </div>
            </template>

            <!-- Created by Column -->
            <template v-slot:item.createdBy="{ item }">
              <!-- Declared in config/job_schedules.php: cron, timezone and context are owned by the code -->
              <v-tooltip v-if="item.declared" bottom>
                <template #activator="{ on, attrs }">
                  <v-chip x-small outlined v-bind="attrs" v-on="on"><v-icon x-small left>mdi-code-tags</v-icon>{{ $t('job-manager.schedules.declared') }}</v-chip>
                </template>
                {{ $t('job-manager.schedules.declared-hint') }}
              </v-tooltip>
              <span v-else-if="!item.createdBy" class="text--secondary">-</span>
              <span v-else-if="item.createdBy === 'SYSTEM'" class="text--secondary">
                <v-icon x-small>mdi-cog-outline</v-icon>
                {{ $t('job-manager.schedules.created-by-system') }}
              </span>
              <span v-else class="text-caption">
                <v-icon x-small>mdi-account-outline</v-icon>
                {{ item.createdBy.id || '-' }}
              </span>
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="item.enabled"
                icon
                small
                color="warning"
                :loading="item._pausing"
                @click="pauseSchedule(item)"
                :title="$t('job-manager.schedules.pause')"
              >
                <v-icon small>mdi-pause</v-icon>
              </v-btn>
              <v-btn
                v-else
                icon
                small
                color="success"
                :loading="item._resuming"
                @click="resumeSchedule(item)"
                :title="$t('job-manager.schedules.resume')"
              >
                <v-icon small>mdi-play</v-icon>
              </v-btn>
              <v-btn
                v-if="!item.declared"
                icon
                small
                color="primary"
                @click="openEditDialog(item)"
                :title="$t('job-manager.schedules.edit')"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="!item.declared"
                icon
                small
                color="error"
                @click="confirmDeleteSchedule(item)"
                :title="$t('delete')"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Scheduled Tasks (One-time scheduled jobs) -->
      <v-row class="mt-6">
        <v-col cols="12">
          <headline>{{ $t('job-manager.scheduled-tasks') }}</headline>
          <v-data-table
            :headers="scheduledHeaders"
            :items="scheduledTasks"
            :items-per-page="10"
            :loading="loading"
            class="elevation-1"
          >
            <!-- Definition Column -->
            <template v-slot:item.jobDefinitionId="{ item }">
              <span class="font-weight-medium">{{ getJobDefinitionName(item) }}</span>
            </template>

            <!-- Scheduled Date Column -->
            <template v-slot:item.scheduledAt="{ item }">
              <span :class="{ 'error--text font-weight-bold': isOverdue(item) }">
                {{ formatDateTime(item.scheduledAt || item.nextRunAt) }}
                <v-icon v-if="isOverdue(item)" small color="error" class="ml-1">mdi-alert</v-icon>
              </span>
            </template>

            <!-- Status Column -->
            <template v-slot:item.status="{ item }">
              <v-chip
                small
                :color="getStatusColor(item.status || (item.enabled ? 'pending' : 'disabled'))"
                text-color="white"
              >
                {{ item.status || (item.enabled ? 'pending' : 'disabled') }}
              </v-chip>
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="item.enabled !== false && item.status !== 'completed'"
                icon
                small
                color="error"
                @click="confirmCancelScheduled(item)"
                :title="$t('job-manager.cancel')"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Cancel Scheduled Task Dialog -->
      <v-dialog v-model="cancelDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">
            {{ $t('job-manager.schedules.confirm-cancel') }}
          </v-card-title>
          <v-card-text>
            {{ $t('job-manager.schedules.confirm-cancel-message') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancelDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              color="error"
              :loading="cancelling"
              @click="executeCancelScheduled"
            >
              {{ $t('confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Create Schedule Dialog -->
      <v-dialog v-model="createDialog" max-width="600" persistent>
        <v-card>
          <v-card-title class="headline">
            {{ $t('job-manager.schedules.create-title') }}
          </v-card-title>
          <v-card-text>
            <v-form ref="createForm" v-model="createFormValid">
              <v-select
                v-model="createForm.jobDefinitionId"
                :items="jobDefinitions"
                item-text="name"
                item-value="id"
                :label="$t('job-manager.definition')"
                :rules="[v => !!v || $t('job-manager.schedules.definition-required')]"
                required
              />

              <v-radio-group
                v-model="createForm.type"
                :label="$t('job-manager.type')"
                row
              >
                <v-radio
                  :label="$t('job-manager.schedules.type-cron')"
                  value="cron"
                />
                <v-radio
                  :label="$t('job-manager.schedules.type-once')"
                  value="once"
                />
              </v-radio-group>

              <!-- Cron Expression (for recurring) -->
              <v-text-field
                v-if="createForm.type === 'cron'"
                v-model="createForm.cronExpression"
                :label="$t('job-manager.schedules.columns.cron')"
                :hint="$t('job-manager.schedules.cron-hint')"
                persistent-hint
                :rules="[v => !!v || $t('job-manager.schedules.cron-required')]"
                required
              />

              <!-- Scheduled Date (for one-time) -->
              <v-menu
                v-if="createForm.type === 'once'"
                v-model="createDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="createForm.scheduledAt"
                    :label="$t('job-manager.schedule-date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="[v => !!v || $t('job-manager.schedules.date-required')]"
                    required
                  />
                </template>
                <v-date-picker
                  v-model="createForm.scheduledAt"
                  @input="createDateMenu = false"
                />
              </v-menu>

              <!-- Timezone -->
              <v-combobox
                v-model="createForm.timezone"
                :items="timezoneOptions"
                :label="$t('job-manager.schedules.timezone-label')"
                :hint="$t('job-manager.schedules.timezone-hint')"
                persistent-hint
                clearable
              />

              <!-- Context (JSON) -->
              <v-textarea
                v-model="createForm.context"
                :label="$t('job-manager.context')"
                :hint="$t('job-manager.schedules.context-hint')"
                persistent-hint
                rows="3"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeCreateDialog">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="creating"
              :disabled="!createFormValid"
              @click="createSchedule"
            >
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Schedule Dialog -->
      <v-dialog v-model="editDialog" max-width="600" persistent>
        <v-card>
          <v-card-title class="headline">
            {{ $t('job-manager.schedules.edit') }}
          </v-card-title>
          <v-card-text>
            <v-form ref="editForm" v-model="editFormValid">
              <v-text-field
                :value="editingSchedule ? (editingSchedule.jobDefinitionName || editingSchedule.jobDefinitionId) : ''"
                :label="$t('job-manager.definition')"
                disabled
              />
              <v-text-field
                v-model="editForm.cronExpression"
                :label="$t('job-manager.schedules.columns.cron')"
                :hint="$t('job-manager.schedules.cron-hint')"
                persistent-hint
                :rules="[v => !!v || $t('job-manager.schedules.cron-required')]"
                required
              />
              <!-- Comme à la création : le fuseau dans lequel la cadence se lit, et le contexte du job -->
              <v-combobox
                v-model="editForm.timezone"
                :items="timezoneOptions"
                :label="$t('job-manager.schedules.timezone-label')"
                :hint="$t('job-manager.schedules.timezone-hint')"
                persistent-hint
                class="mt-4"
              />
              <v-textarea
                v-model="editForm.context"
                :label="$t('job-manager.context')"
                :hint="$t('job-manager.schedules.context-hint')"
                persistent-hint
                rows="3"
                class="mt-4"
              />
              <v-switch
                v-model="editForm.enabled"
                :label="$t('job-manager.schedules.columns.enabled')"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeEditDialog">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="updating"
              :disabled="!editFormValid"
              @click="updateSchedule"
            >
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Schedule Confirmation Dialog -->
      <v-dialog v-model="deleteScheduleDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">
            {{ $t('job-manager.schedules.confirm-delete') }}
          </v-card-title>
          <v-card-text>
            {{ $t('job-manager.schedules.confirm-delete-message') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteScheduleDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              color="error"
              :loading="deleting"
              @click="deleteSchedule"
            >
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import JobRepository from '@repository/JobRepository'

export default {
  name: 'Schedules',

  data() {
    return {
      loading: false,
      executingOverdue: false,
      schedules: [],
      filters: {
        status: null,
        type: null
      },
      cancelDialog: false,
      cancelling: false,
      taskToCancel: null,
      // Create dialog
      createDialog: false,
      createDateMenu: false,
      creating: false,
      createFormValid: false,
      createForm: {
        jobDefinitionId: null,
        type: 'cron',
        cronExpression: '',
        scheduledAt: null,
        timezone: 'UTC',
        context: ''
      },
      jobDefinitions: [],
      // Edit dialog
      editDialog: false,
      editingSchedule: null,
      updating: false,
      editFormValid: false,
      editForm: {
        cronExpression: '',
        enabled: true,
        timezone: 'UTC',
        context: '',
      },
      // Delete dialog
      deleteScheduleDialog: false,
      scheduleToDelete: null,
      deleting: false
    }
  },

  computed: {
    statusOptions() {
      return [
        { text: this.$t('job-manager.schedules.enabled'), value: 'enabled' },
        { text: this.$t('job-manager.schedules.disabled'), value: 'disabled' },
        { text: this.$t('job-manager.schedules.overdue'), value: 'overdue' }
      ]
    },
    typeOptions() {
      return [
        { text: this.$t('job-manager.schedules.type-cron'), value: 'cron' },
        { text: this.$t('job-manager.schedules.type-once'), value: 'once' }
      ]
    },
    timezoneOptions() {
      return [
        'UTC',
        'Europe/Paris',
        'Europe/London',
        'Europe/Brussels',
        'Europe/Amsterdam',
        'America/New_York',
        'America/Los_Angeles',
      ]
    },
    recurringHeaders() {
      return [
        { text: this.$t('job-manager.schedules.columns.definition'), value: 'jobDefinitionId' },
        { text: this.$t('job-manager.schedules.columns.cron'), value: 'cronExpression', width: '260px' },
        { text: this.$t('job-manager.schedules.columns.enabled'), value: 'enabled', width: '160px' },
        { text: this.$t('job-manager.schedules.columns.next-run'), value: 'nextRunAt', width: '200px' },
        { text: this.$t('job-manager.schedules.columns.last-run'), value: 'lastRunAt', width: '220px' },
        { text: this.$t('job-manager.schedules.columns.created-by'), value: 'createdBy', width: '140px' },
        { text: this.$t('job-manager.actions'), value: 'actions', sortable: false, width: '120px' }
      ]
    },
    scheduledHeaders() {
      return [
        { text: this.$t('job-manager.schedules.columns.id'), value: 'id', width: '100px' },
        { text: this.$t('job-manager.schedules.columns.definition'), value: 'jobDefinitionId' },
        { text: this.$t('job-manager.schedule-date'), value: 'scheduledAt', width: '180px' },
        { text: this.$t('job-manager.status'), value: 'status', width: '120px' },
        { text: this.$t('job-manager.user'), value: 'user', width: '150px' },
        { text: this.$t('job-manager.actions'), value: 'actions', sortable: false, width: '100px' }
      ]
    },
    recurringTasks() {
      return this.schedules.filter(s => s.type === 'cron' || s.cronExpression)
    },
    scheduledTasks() {
      return this.schedules.filter(s => s.type === 'once' || (!s.cronExpression && s.scheduledAt))
    }
  },

  mounted() {
    this.loadSchedules()
    this.loadJobDefinitions()
  },

  methods: {
    async loadSchedules() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.status === 'enabled') {
          params.enabled = true
        } else if (this.filters.status === 'disabled') {
          params.enabled = false
        } else if (this.filters.status === 'overdue') {
          params.overdue = true
        }
        if (this.filters.type) {
          params.type = this.filters.type
        }

        const result = await JobRepository.schedules(params)
        this.schedules = result.schedules || result.items || result || []
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },

    async toggleEnabled(schedule) {
      if (schedule.enabled) {
        await this.pauseSchedule(schedule)
      } else {
        await this.resumeSchedule(schedule)
      }
    },

    async pauseSchedule(schedule) {
      this.$set(schedule, '_pausing', true)
      this.$set(schedule, '_loading', true)
      try {
        await JobRepository.pauseSchedule(schedule.id)
        schedule.enabled = false
        this.notifySuccess('job-manager.schedules.pause-success')
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.$set(schedule, '_pausing', false)
        this.$set(schedule, '_loading', false)
      }
    },

    async resumeSchedule(schedule) {
      this.$set(schedule, '_resuming', true)
      this.$set(schedule, '_loading', true)
      try {
        await JobRepository.resumeSchedule(schedule.id)
        schedule.enabled = true
        this.notifySuccess('job-manager.schedules.resume-success')
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.$set(schedule, '_resuming', false)
        this.$set(schedule, '_loading', false)
      }
    },

    async executeOverdue() {
      this.executingOverdue = true
      try {
        const result = await JobRepository.executeSchedules()
        const count = result.executed || result.count || 0
        this.notifySuccess('job-manager.schedules.execute-success', { count })
        this.loadSchedules()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.executingOverdue = false
      }
    },

    confirmCancelScheduled(task) {
      this.taskToCancel = task
      this.cancelDialog = true
    },

    async executeCancelScheduled() {
      if (!this.taskToCancel) return

      this.cancelling = true
      try {
        await JobRepository.pauseSchedule(this.taskToCancel.id)
        this.notifySuccess('job-manager.schedules.cancel-success')
        this.cancelDialog = false
        this.taskToCancel = null
        this.loadSchedules()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.cancelling = false
      }
    },

    resetFilters() {
      this.filters = {
        status: null,
        type: null
      }
      this.loadSchedules()
    },

    isOverdue(schedule) {
      const nextRun = schedule.nextRunAt || schedule.scheduledAt
      if (!nextRun || !schedule.enabled) return false
      return new Date(nextRun) < new Date()
    },

    formatDateTime(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString()
    },

    formatDateTimeInTz(date, tz) {
      if (!date) return '-'
      try {
        return new Date(date).toLocaleString(undefined, { timeZone: tz || 'UTC' })
      } catch (e) {
        return new Date(date).toLocaleString()
      }
    },

    browserTimezone() {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch (e) {
        return null
      }
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        active: 'green',
        enabled: 'green',
        disabled: 'grey',
        completed: 'blue',
        cancelled: 'red'
      }
      return colors[status] || 'grey'
    },

    getRunStatusColor(status) {
      const colors = {
        SUCCESS: 'success',
        FAILED: 'error',
        RUNNING: 'primary',
        PENDING: 'grey lighten-1',
        CANCELED: 'grey',
        PAUSED: 'orange',
      }
      return colors[status] || 'grey'
    },

    getCronDescription(cron) {
      if (!cron) return ''
      const parts = cron.trim().split(/\s+/)
      if (parts.length < 5) return cron

      const presets = {
        '* * * * *': this.$t('job-manager.schedules.cron-every-minute'),
        '0 * * * *': this.$t('job-manager.schedules.cron-every-hour'),
        '0 0 * * *': this.$t('job-manager.schedules.cron-every-day'),
        '0 0 * * 0': this.$t('job-manager.schedules.cron-every-week'),
        '0 0 1 * *': this.$t('job-manager.schedules.cron-every-month'),
      }
      if (presets[cron]) return presets[cron]

      const [minute, hour, dom, month, dow] = parts
      const isNum = v => /^\d+$/.test(v)
      const pad = v => String(v).padStart(2, '0')
      const time = (isNum(hour) && isNum(minute)) ? `${pad(hour)}:${pad(minute)}` : null
      const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']

      // Monthly on a specific day at a specific time: m h D * *
      if (time && isNum(dom) && month === '*' && dow === '*') {
        const dayLabel = dom === '1' ? '1er' : dom
        return `Le ${dayLabel} de chaque mois à ${time}`
      }
      // Weekly on a specific weekday at a specific time: m h * * d
      if (time && dom === '*' && month === '*' && isNum(dow)) {
        return `Chaque ${dayNames[Number(dow) % 7]} à ${time}`
      }
      // Daily at a specific time: m h * * *
      if (time && dom === '*' && month === '*' && dow === '*') {
        return `Tous les jours à ${time}`
      }
      // Yearly on a specific date: m h D M *
      if (time && isNum(dom) && isNum(month) && dow === '*') {
        const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
        return `Le ${dom} ${months[Number(month) - 1] || month} à ${time}`
      }
      return cron
    },

    getJobDefinitionName(item) {
      if (item && item.jobDefinitionName) return item.jobDefinitionName
      const def = this.jobDefinitions.find(d => d.id === item.jobDefinitionId)
      if (def) return def.name
      return item.jobDefinitionId
    },

    // Create schedule methods
    async loadJobDefinitions() {
      try {
        // Only load active definitions (not disabled or orphan)
        const result = await JobRepository.jobDefinitions({ status: 'active' })
        this.jobDefinitions = result.definitions || result || []
      } catch (error) {
        console.error('Failed to load job definitions', error)
      }
    },

    openCreateDialog() {
      this.createForm = {
        jobDefinitionId: null,
        type: 'cron',
        cronExpression: '',
        scheduledAt: null,
        timezone: 'UTC',
        context: ''
      }
      this.createDialog = true
    },

    closeCreateDialog() {
      this.createDialog = false
      if (this.$refs.createForm) {
        this.$refs.createForm.reset()
      }
    },

    async createSchedule() {
      if (!this.$refs.createForm.validate()) return

      this.creating = true
      try {
        const payload = {
          jobDefinitionId: this.createForm.jobDefinitionId,
          type: this.createForm.type
        }

        if (this.createForm.type === 'cron') {
          payload.cronExpression = this.createForm.cronExpression
        } else {
          payload.scheduledAt = this.createForm.scheduledAt
        }

        if (this.createForm.timezone) {
          payload.timezone = this.createForm.timezone
        }

        if (this.createForm.context) {
          try {
            payload.context = JSON.parse(this.createForm.context)
          } catch (e) {
            payload.context = this.createForm.context
          }
        }

        await JobRepository.createSchedule(payload)
        this.notifySuccess('job-manager.schedules.create-success')
        this.closeCreateDialog()
        this.loadSchedules()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.creating = false
      }
    },

    // Edit schedule methods
    openEditDialog(schedule) {
      this.editingSchedule = schedule
      this.editForm = {
        cronExpression: schedule.cronExpression || '',
        enabled: schedule.enabled,
        timezone: schedule.timezone || 'UTC',
        context: schedule.context && Object.keys(schedule.context).length ? JSON.stringify(schedule.context, null, 2) : '',
      }
      this.editDialog = true
    },

    closeEditDialog() {
      this.editDialog = false
      this.editingSchedule = null
      if (this.$refs.editForm) {
        this.$refs.editForm.reset()
      }
    },

    async updateSchedule() {
      if (!this.$refs.editForm.validate()) return

      this.updating = true
      try {
        let context = {}
        if (this.editForm.context && this.editForm.context.trim()) {
          try {
            context = JSON.parse(this.editForm.context)
          } catch (e) {
            this.notifyError('job-manager.schedules.context-invalid')
            return
          }
        }
        await JobRepository.updateSchedule(this.editingSchedule.id, {
          cronExpression: this.editForm.cronExpression,
          enabled: this.editForm.enabled,
          timezone: this.editForm.timezone || 'UTC',
          // Vide : le contexte est vidé (le serveur garde l'ancien seulement si le champ n'est pas envoyé)
          context,
        })
        this.notifySuccess('job-manager.schedules.update-success')
        this.closeEditDialog()
        this.loadSchedules()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.updating = false
      }
    },

    // Delete schedule methods
    confirmDeleteSchedule(schedule) {
      this.scheduleToDelete = schedule
      this.deleteScheduleDialog = true
    },

    async deleteSchedule() {
      if (!this.scheduleToDelete) return

      this.deleting = true
      try {
        await JobRepository.deleteSchedule(this.scheduleToDelete.id)
        this.notifySuccess('job-manager.schedules.delete-success')
        this.deleteScheduleDialog = false
        this.scheduleToDelete = null
        this.loadSchedules()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-manager-schedules {
  padding: 20px;

  code {
    background-color: #f5f5f5;
    border-radius: 4px;
  }
}
</style>
